import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {STATUS_MODEL} from "../../../../enums/statusEnum";
import {
    ApprovalMaterialRequest,
    GetMaterialRequestById
} from "../../../../reduxToolkit/MaterialRequest/MaterialRequestApi";
import {CustomFieldCardStyle} from "../../../../components/Styles";
import {PHOTO_PICKER_MODEL} from "../../../../components/PhotoPicker/SubComponent/Models/Models";
import {toast} from "react-toastify";
import {getToastMessageByStatus} from "../../ApprovalTracker/Model/Model";
import {errorMsg} from "../../../../Apis/functions";
import {MATERIAL_REQUEST_APPROVAL_MODEL} from "../Model/Model";
import routes from "../../../../Routes/routes";
import {getStatusConfig} from "../../Visitor/Request/Model/Model";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 */
export const ApprovalMaterialRequestApp = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {id} = useParams();
    const navigate = useNavigate();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const [showApprove, setShowApprove] = useState(false);
    const [showDenied, setShowDenied] = useState(false);
    const [modified, setModified] = useState(false);
    const [row, setRow] = useState(null);
    const [resultsToShow, setResultsToShow] = useState(null);
    const [disabled, setDisabled] = useState(false);
    // Holds the current card styling config, recalculated on invitation status change
    const [currentStatusConfig, setCurrentStatusConfig] = useState(null);
    const [showButtonsToApprove, setShowButtonsToApprove] = useState(false);

    // Generate styles based on approval status
    const styles = CustomFieldCardStyle(currentStatusConfig?.id, disabled);

    // Generic modal configuration for approval/denial/comments
    const genericCommentModals = [
        {
            show: showApprove,
            setter: setShowApprove,
            title: 'approve_request',
            setRow: setRow,
            row: row,
            status: STATUS_MODEL.APPROVED,
            requiredComment: false,
        },
        {
            show: showDenied,
            setter: setShowDenied,
            title: 'decline_comment',
            setRow: setRow,
            row: row,
            status: STATUS_MODEL.DENIED,
            requiredComment: true
        }
    ];

    /**
     * Updates the approval action visibility and current status configuration
     * whenever the material request approval status changes.
     *
     * Rules:
     * - Displays approval buttons when the request is pending review.
     * - Hides approval buttons for final statuses such as approved,
     * denied, canceled, or changes requested.
     * - Updates the visual status configuration used by the UI.
     */
    useEffect(() => {
        if(!resultsToShow) return;
        if(resultsToShow?.approvalStatusId === STATUS_MODEL.PENDING_REVIEW){
            setShowButtonsToApprove(true);
            return;
        }

        const aux =  getStatusConfig(resultsToShow?.approvalStatusId);
        if(aux !== null){
            setShowButtonsToApprove(aux.id !== STATUS_MODEL.DENIED &&
                aux.id !== STATUS_MODEL.APPROVED &&
                aux.id !== STATUS_MODEL.CANCELED &&
                aux.id !== STATUS_MODEL.CHANGES_REQUESTED);
        }

        setCurrentStatusConfig(aux);
    }, [resultsToShow?.approvalStatusId, modified]);


    /**
     * Loads material request information on component initialization.
     */
    useEffect(() => {
        getData();
    }, []);

    /**
     * Reloads material request information after a successful modification.
     * Resets the modified flag once the refresh completes.
     */
    useEffect(() => {
        if (modified) {
            getData();
            setModified(false);
        }
    }, [modified]);

    /**
     * Retrieves the material request detail information from the backend.
     *
     * The response data is transformed before being stored in state:
     * - Evidence files are mapped into PHOTO_PICKER_MODEL instances
     *   for UI compatibility.
     *
     * @returns {void}
     */
    const getData = () => {
        dispatch(GetMaterialRequestById(id)).then(({ payload: { data: { data, success } } }) => {
            if (success === true) {
                setResultsToShow({...data, evidence: data?.evidence?.map(e => PHOTO_PICKER_MODEL.toSet(e))});
            }
        });
    };

    /**
     * Navigates back to the material request main view.
     *
     * @returns {void}
     */
    const handleOnBack = () => {
        navigate(routes.materialRequest.main);
    }

    /**
     * Handles the approval or denial action for a material request.
     *
     * Sends the selected status and approval reason to the backend.
     * If the operation succeeds:
     * - Refreshes the request information.
     * - Displays a success toast notification.
     *
     * If the operation fails:
     * - Displays the corresponding backend error message.
     *
     * @param {string} approvalReason - Comment or reason associated with the action.
     * @param {number} approvalStatusId - Approval status identifier to apply.
     *
     * @returns {void}
     */
    const handleOnClick = (approvalReason, approvalStatusId) => {
        dispatch(ApprovalMaterialRequest(MATERIAL_REQUEST_APPROVAL_MODEL.toRegister({
            id: resultsToShow?.id,
            approvalStatusId,
            approvalReason
        }))).then(({ payload: { data: { success, code } } }) => {
            if (success) {
                setModified(true);
                toast.success(t(getToastMessageByStatus(approvalStatusId)));
            } else {
                errorMsg(code);
            }
        });
    }

    return {
        DEV,
        permission,
        navigate,
        resultsToShow,
        setShowApprove,
        setShowDenied,
        setRow,
        styles,
        genericCommentModals,
        t,
        currentStatusConfig,
        handleOnBack,
        showButtonsToApprove,
        handleOnClick
    };
}
